import { useEffect, useState } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mail, CheckCircle2, XCircle, Send, Info } from "lucide-react";

export default function AdminEmails() {
  return <PortalLayout sidebar="admin"><AdminEmailsBody /></PortalLayout>;
}

export function AdminEmailsBody() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);

  const loadLogs = () => api.get("/admin/email/logs?limit=50").then((r) => setLogs(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/admin/email/status").then((r) => setStatus(r.data)).catch(() => {});
    loadLogs();
    const t = setInterval(loadLogs, 8000);
    return () => clearInterval(t);
  }, []);

  const sendTest = async (e) => {
    e.preventDefault();
    if (!testTo) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/email/test", { to: testTo });
      if (data.ok) toast.success(`Test envoyé à ${testTo}`);
      else toast.error("Envoi échoué — consultez le journal ci-dessous");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setSending(false);
    loadLogs();
  };

  if (!status) return <div className="p-8">Chargement…</div>;

  return (
    <div className="space-y-6">
        <div>
          <h1 className="font-serif text-4xl text-navy">Emails transactionnels</h1>
          <p className="mt-1 text-sm text-neutral-600">Configuration et statut de l&apos;intégration Brevo (Sendinblue).</p>
        </div>

        <div className="rounded-2xl border border-neutral-100 bg-white p-6" data-testid="email-status">
          <h2 className="text-lg font-bold text-navy inline-flex items-center gap-2"><Mail className="h-5 w-5 text-wine-500" /> Statut de la configuration</h2>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex items-center justify-between rounded-lg bg-neutral-50 px-4 py-3">
              <span className="font-semibold text-neutral-700">Clé API Brevo</span>
              {status.configured
                ? <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Configurée</span>
                : <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-100 px-3 py-1 text-xs font-bold text-rose-700"><XCircle className="h-3.5 w-3.5" /> Manquante</span>}
            </div>
            <div className="flex items-center justify-between rounded-lg bg-neutral-50 px-4 py-3">
              <span className="font-semibold text-neutral-700">Email expéditeur</span>
              <span className="font-mono text-xs text-neutral-700">{status.sender_name} &lt;{status.sender_email}&gt;</span>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-neutral-100 bg-white p-6">
          <h2 className="text-lg font-bold text-navy inline-flex items-center gap-2"><Send className="h-5 w-5 text-wine-500" /> Envoyer un email de test</h2>
          <p className="mt-1 text-sm text-neutral-600">Envoyez un email de test pour vérifier que la configuration fonctionne bout en bout.</p>
          <form onSubmit={sendTest} className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="text-xs font-semibold text-neutral-700">Destinataire</label>
              <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} required placeholder="vous@exemple.com"
                     className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="email-test-to" />
            </div>
            <button type="submit" disabled={sending || !status.configured}
                    className="rounded-full bg-wine-500 px-5 py-2.5 text-xs font-bold text-white hover:bg-wine-600 disabled:opacity-50"
                    data-testid="email-test-send">
              {sending ? "Envoi…" : "Envoyer le test"}
            </button>
          </form>
        </div>

        <div className="rounded-2xl border border-wine-100 bg-wine-50/30 p-5 text-sm text-neutral-700">
          <div className="inline-flex items-center gap-2 font-bold text-wine-700"><Info className="h-4 w-4" /> Emails déclenchés automatiquement</div>
          <ul className="mt-2 list-disc pl-5 space-y-1">
            <li>Inscription événement confirmée (gratuit) ou après paiement Stripe</li>
            <li>RDV 1:1 individuel réservé → notification au visiteur + au contact exposant</li>
            <li>Validation inscription Exposant (admin ou paiement Stripe complété)</li>
            <li>Candidature membre reçue → accusé de réception</li>
          </ul>
          <p className="mt-3 text-xs text-neutral-600">
            ⚠️ Si l&apos;envoi échoue, vérifiez les <a href="https://app.brevo.com/security/authorised_ips" target="_blank" rel="noopener noreferrer" className="font-bold text-wine-700 underline">IPs autorisées</a> dans votre compte Brevo (ou désactivez cette protection).
          </p>
        </div>

        {/* Email logs */}
        <div className="rounded-2xl border border-neutral-100 bg-white p-6" data-testid="email-logs">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-navy inline-flex items-center gap-2"><Mail className="h-5 w-5 text-wine-500" /> Journal des envois (50 derniers)</h2>
            <button onClick={loadLogs} className="rounded-full border border-neutral-200 px-3 py-1 text-xs font-semibold hover:bg-neutral-50">Rafraîchir</button>
          </div>
          <p className="mt-1 text-xs text-neutral-600">Actualisation auto toutes les 8s. Statut : <strong>sent</strong> = OK · <strong>failed</strong> = erreur Brevo · <strong>skipped_no_config</strong> = clé manquante côté serveur.</p>
          <div className="mt-4 overflow-hidden rounded-xl border border-neutral-100">
            <table className="w-full text-xs">
              <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
                <tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">À</th><th className="px-3 py-2 text-left">Sujet</th><th className="px-3 py-2 text-left">Statut</th><th className="px-3 py-2 text-left">Détail</th></tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-t border-neutral-100" data-testid={`log-${l.id}`}>
                    <td className="px-3 py-2 text-neutral-500">{(l.created_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="px-3 py-2 font-mono">{(l.to || []).join(", ")}</td>
                    <td className="px-3 py-2">{l.subject}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 font-bold ${l.status === "sent" ? "bg-emerald-100 text-emerald-700" : l.status === "failed" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                        {l.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-neutral-500">
                      {l.message_id && <span className="font-mono text-[10px]">{l.message_id.slice(0, 30)}…</span>}
                      {l.error && <span className="text-rose-600 text-[11px]">{l.error.slice(0, 80)}</span>}
                      {l.http_status && !l.message_id && <span> HTTP {l.http_status}</span>}
                    </td>
                  </tr>
                ))}
                {logs.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-neutral-500">Aucun envoi enregistré.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
  );
}
